// src/pages/services/ServiceOrderQuoteEditorPage.tsx
// Editor de orçamento vinculado à OS — itens agrupados por equipamento.
// Espelha a mecânica do EditQuotation (modal de item, desconto, cálculos, save).
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useServiceOrder } from '@/hooks/useServiceOrders';
import { useQuotations } from '@/hooks/useQuotations';
import { quotationFormSchemaEditPage } from '@/lib/validations/orderForm';
import {
  calculateItemTotals,
  calculateOrderSummary,
  calculateGlobalDiscount,
  roundMoney,
} from '@/utils/orderCalculator';
import { normalizeDiscountValueToPerUnit } from '@/utils/discountNormalizer';
import { toLocalDate } from '@/utils/dateHelpers';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CalendarIcon,
  ArrowLeft,
  Plus,
  Trash2,
  Wrench,
  Loader2,
  Truck,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { DiscountSelector } from '@/components/Sales/DiscountSelector';
import { PaymentConditionGroupedSelector } from '@/components/Sales/PaymentConditionGroupedSelector';
import { ProductServiceCombobox } from '@/components/Sales/ProductServiceCombobox';
import { ProductSelectionModal } from '@/components/Sales/ProductSelectionModal';
import { ServiceSelectionModal } from '@/components/Services/ServiceSelectionModal';
import { formatCurrency } from '@/utils/formatters';

// ── Tipos ────────────────────────────────────────────────────────────────────

type QuoteFormValues = z.infer<typeof quotationFormSchemaEditPage>;

interface ItemState {
  type: 'product' | 'service';
  product_id: string;
  service_id: string;
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  discount_value: number;        // por unidade — igual ao EditQuotation
  discount_type: 'percentage' | 'fixed' | null;
  notes: string;
  service_order_equipment_id: string; // CRÍTICO — preserva agrupamento no delete+reinsert
}

// ── Página ───────────────────────────────────────────────────────────────────

export default function ServiceOrderQuoteEditorPage() {
  const { id } = useParams<{ id: string }>(); // OS id
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    data: order,
    isLoading: orderLoading,
    isFetching: orderFetching,
  } = useServiceOrder(id ?? null);
  const { updateQuotation } = useQuotations();

  const [items, setItems] = useState<ItemState[]>([]);

  // Estado do ProductSelectionModal (produtos)
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = useState<any>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null);

  // Estado do ServiceSelectionModal (serviços)
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [currentServiceIndex, setCurrentServiceIndex] = useState<number | null>(null);
  const [selectedServiceForModal, setSelectedServiceForModal] = useState<{
    id: string; name: string; sale_price?: number;
  } | null>(null);
  const [serviceCostPrice, setServiceCostPrice] = useState<number | null>(null);
  const [serviceInitialValues, setServiceInitialValues] = useState<{
    quantity: number; unit_price: number;
    discount_percentage: number; discount_value: number;
    discount_type: 'percentage' | 'fixed' | null; notes: string;
  } | undefined>(undefined);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quotationFormSchemaEditPage),
    defaultValues: {
      customer_id: '',
      quotation_date: new Date(),
      valid_until: new Date(),
      status: 'draft' as const,
      discount_percentage: 0,
      discount_value: 0,
      freight_value: 0,
    },
  });

  // Redireciona se OS não tem orçamento vinculado.
  // Guard só dispara quando a query está settled (não loading nem fetching)
  // para não disparar durante o fetch inicial após a navegação pós-seed.
  useEffect(() => {
    if (!orderLoading && !orderFetching && order && !order.quotation_id) {
      toast({ title: 'OS sem orçamento vinculado', variant: 'destructive' });
      navigate(`/dashboard/services/${id}`);
    }
  }, [order, orderLoading, orderFetching, id, navigate, toast]);

  // Carrega quotation + items (inclui service_order_equipment_id via *)
  const quotationId = order?.quotation_id ?? null;
  const { data: quotationData, isLoading: quotationLoading } = useQuery({
    queryKey: ['so-quote-editor', quotationId],
    queryFn: async () => {
      if (!quotationId) return null;
      const { data: quotation, error: qErr } = await supabase
        .from('quotations')
        .select('*')
        .eq('id', quotationId)
        .single();
      if (qErr) throw qErr;

      const { data: qItems, error: iErr } = await supabase
        .from('quotation_items')
        .select('*')
        .eq('quotation_id', quotationId);
      if (iErr) throw iErr;

      return { quotation, items: qItems ?? [] };
    },
    enabled: !!quotationId,
  });

  // Hidrata form + items ao receber dados (espelha EditQuotation)
  useEffect(() => {
    if (!quotationData) return;
    const q = quotationData.quotation;

    form.reset({
      customer_id:          q.customer_id,
      quotation_date:       toLocalDate(q.quotation_date),
      valid_until:          toLocalDate(q.valid_until),
      status:               q.status,
      payment_condition_id: q.payment_condition_id || '',
      delivery_deadline:    q.delivery_deadline || '',
      notes:                q.notes || '',
      fiscal_notes:         q.fiscal_notes || '',
      discount_percentage:  Number(q.discount_percentage) || 0,
      discount_value:       Number(q.discount_value) || 0,
      freight_value:        Number(q.freight_value) || 0,
    });

    const normalized: ItemState[] = quotationData.items.map((item: any) => {
      const quantity  = Number(item.quantity) || 1;
      const unitPrice = Number(item.unit_price);
      const normalizedDiscountValue = normalizeDiscountValueToPerUnit({
        discount_value:      Number(item.discount_value || 0),
        discount_percentage: Number(item.discount_percentage || 0),
        unit_price:          unitPrice,
        quantity,
      });
      return {
        type:                       (item.item_type || 'product') as 'product' | 'service',
        product_id:                 item.product_id || '',
        service_id:                 item.service_id || '',
        quantity,
        unit_price:                 unitPrice,
        discount_percentage:        Number(item.discount_percentage) || 0,
        discount_value:             normalizedDiscountValue,
        discount_type:              item.discount_type || null,
        notes:                      item.notes || '',
        service_order_equipment_id: item.service_order_equipment_id || '',
      };
    });
    setItems(normalized);
  }, [quotationData]);

  // ── Helpers de item (espelhos do EditQuotation) ───────────────────────────

  const updateItemBatch = (index: number, updates: Partial<ItemState>) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const addItemToEquipment = (equipmentId: string) => {
    setItems((prev) => [
      ...prev,
      {
        type:                       'product',
        product_id:                 '',
        service_id:                 '',
        quantity:                   1,
        unit_price:                 0,
        discount_percentage:        0,
        discount_value:             0,
        discount_type:              null,
        notes:                      '',
        service_order_equipment_id: equipmentId,
      },
    ]);
  };

  const handleProductSelected = (index: number, product: any) => {
    setCurrentItemIndex(index);
    setSelectedProductForModal(product);
    setProductModalOpen(true);
  };

  const handleModalConfirm = (data: any) => {
    if (currentItemIndex !== null && selectedProductForModal) {
      updateItemBatch(currentItemIndex, {
        product_id:          selectedProductForModal.id,
        quantity:            data.quantity,
        unit_price:          data.unit_price,
        discount_percentage: data.discount_percentage,
        discount_value:      data.discount_value,
        discount_type:       data.discount_type,
        notes:               data.notes,
      });
    }
    setCurrentItemIndex(null);
    setSelectedProductForModal(null);
  };

  // S1/S2 — abre ServiceSelectionModal para serviço novo (vindo do onChange)
  const handleServiceSelected = async (
    index: number,
    serviceId: string,
    serviceName: string,
    salePriceFromCombobox: number,
  ) => {
    // S3 — busca cost_price ANTES de abrir o modal: sem janela de costPrice=null
    let costPrice: number | null = null;
    try {
      const { data } = await supabase
        .from('services')
        .select('cost_price')
        .eq('id', serviceId)
        .eq('company_id', order?.company_id ?? '')
        .single();
      costPrice = (data as any)?.cost_price ?? null;
    } catch {
      // erro → null = sem trava (regra: sem custo = sem trava)
    }
    setCurrentServiceIndex(index);
    setSelectedServiceForModal({ id: serviceId, name: serviceName, sale_price: salePriceFromCombobox });
    setServiceInitialValues(undefined);
    setServiceCostPrice(costPrice);
    setServiceModalOpen(true);
  };

  // S1 — reabre o modal para linha de serviço já preenchida (edição)
  const handleEditService = async (index: number, item: ItemState) => {
    if (!item.service_id) return;
    // S3 — busca service data + cost_price ANTES de abrir o modal
    let serviceData: { id: string; name: string; sale_price?: number } | null = null;
    let costPrice: number | null = null;
    try {
      const { data } = await supabase
        .from('services')
        .select('id, service_name, sale_price, cost_price')
        .eq('id', item.service_id)
        .eq('company_id', order?.company_id ?? '')
        .single();
      if (data) {
        serviceData = {
          id:         (data as any).id,
          name:       (data as any).service_name,
          sale_price: (data as any).sale_price ?? undefined,
        };
        costPrice = (data as any).cost_price ?? null;
      }
    } catch {
      // erro → serviceData permanece null; modal não abre sem dados do serviço
    }
    if (!serviceData) return; // não abre se não conseguiu carregar
    setCurrentServiceIndex(index);
    setSelectedServiceForModal(serviceData);
    setServiceInitialValues({
      quantity:            item.quantity,
      unit_price:          item.unit_price,
      discount_percentage: item.discount_percentage,
      discount_value:      item.discount_value,
      discount_type:       item.discount_type,
      notes:               item.notes,
    });
    setServiceCostPrice(costPrice);
    setServiceModalOpen(true);
  };

  // Confirm do ServiceSelectionModal — grava na linha via updateItemBatch
  const handleServiceModalConfirm = (data: any) => {
    if (currentServiceIndex !== null && selectedServiceForModal) {
      updateItemBatch(currentServiceIndex, {
        service_id:          selectedServiceForModal.id,
        quantity:            data.quantity,
        unit_price:          data.unit_price,
        discount_percentage: data.discount_percentage,
        discount_value:      data.discount_value,
        discount_type:       data.discount_type,
        notes:               data.notes || '',
      });
    }
    setCurrentServiceIndex(null);
    setSelectedServiceForModal(null);
    setServiceCostPrice(null);
    setServiceInitialValues(undefined);
  };

  // ── Cálculos (espelhos do EditQuotation) ─────────────────────────────────

  const calculateItemTotal = (item: ItemState) => calculateItemTotals(item).total;

  const calculateSubtotal = () => calculateOrderSummary(items).netSubtotal;

  const calculateTotal = () => {
    const summary            = calculateOrderSummary(items);
    const discountPercentage = form.watch('discount_percentage') || 0;
    const discountValue      = form.watch('discount_value') || 0;
    const freightValue       = form.watch('freight_value') || 0;
    const globalDiscount     = calculateGlobalDiscount(summary.netSubtotal, discountPercentage, discountValue);
    return roundMoney(summary.netSubtotal - globalDiscount + freightValue);
  };

  const calculateTotalDiscount = () => {
    const summary            = calculateOrderSummary(items);
    const discountPercentage = form.watch('discount_percentage') || 0;
    const discountValue      = form.watch('discount_value') || 0;
    const globalDiscount     = calculateGlobalDiscount(summary.netSubtotal, discountPercentage, discountValue);
    return roundMoney(summary.totalDiscount + globalDiscount);
  };

  // ── Save (espelha EditQuotation + service_order_equipment_id por item) ────

  const onSubmit = async (data: QuoteFormValues) => {
    if (!quotationId) return;

    const validItems = items.filter(
      (item) =>
        (item.type === 'product' && item.product_id) ||
        (item.type === 'service' && item.service_id),
    );

    if (validItems.length === 0) {
      toast({
        title: 'Adicione pelo menos um item válido',
        description: 'Selecione um produto ou serviço antes de salvar.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const subtotal     = calculateSubtotal();
      const discount     = data.discount_value > 0
        ? roundMoney(data.discount_value)
        : roundMoney((subtotal * data.discount_percentage) / 100);
      const freightValue = roundMoney(data.freight_value || 0);
      const total        = roundMoney(subtotal - discount + freightValue);

      const quotationPayload: Record<string, any> = {
        customer_id:          data.customer_id,
        quotation_date:       data.quotation_date instanceof Date
          ? format(data.quotation_date, 'yyyy-MM-dd')
          : data.quotation_date,
        valid_until:          data.valid_until instanceof Date
          ? format(data.valid_until, 'yyyy-MM-dd')
          : data.valid_until,
        // status omitido intencionalmente — preserva o valor atual no banco
        payment_condition_id: data.payment_condition_id || null,
        delivery_deadline:    data.delivery_deadline || null,
        notes:                data.notes || null,
        fiscal_notes:         data.fiscal_notes || null,
        discount_percentage:  data.discount_percentage || 0,
        discount_value:       discount,
        freight_value:        freightValue,
        subtotal,
        total_value:          total,
      };

      // CRÍTICO: service_order_equipment_id preserva o agrupamento após o
      // delete+reinsert que o updateQuotation faz via {...item} spread.
      const itemsData = validItems.map((item) => ({
        item_type:                  item.type,
        product_id:                 item.type === 'product' ? item.product_id : null,
        service_id:                 item.type === 'service' ? item.service_id : null,
        quantity:                   item.quantity,
        unit_price:                 item.unit_price,
        discount_percentage:        item.discount_percentage || 0,
        discount_value:             item.discount_value || 0,
        discount_type:              item.discount_type || null,
        subtotal:                   roundMoney(item.quantity * item.unit_price),
        total:                      calculateItemTotal(item),
        notes:                      item.notes || null,
        service_order_equipment_id: item.service_order_equipment_id || null,
      }));

      await updateQuotation.mutateAsync({
        id:    quotationId,
        data:  quotationPayload,
        items: itemsData,
      });

      toast({ title: 'Orçamento salvo com sucesso!' });
    } catch (error: any) {
      toast({
        title:       'Erro ao salvar orçamento',
        description: error?.message || 'Tente novamente.',
        variant:     'destructive',
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const equipment  = order?.service_order_equipment ?? [];
  const isLoading  = orderLoading || quotationLoading;
  const customerName = order?.customers?.name ?? 'Cliente não informado';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">Ordem de serviço não encontrada.</p>
        <Button variant="outline" onClick={() => navigate('/dashboard/services')}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col min-h-screen bg-background"
      >
        {/* ── Header ── */}
        <div className="border-b bg-card px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/dashboard/services/${id}`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">
                Orçamento da OS {order.service_order_number}
              </h1>
              <p className="text-sm text-muted-foreground">{customerName}</p>
            </div>
          </div>
          <Button type="submit" disabled={updateQuotation.isPending}>
            {updateQuotation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando…
              </>
            ) : (
              'Salvar Orçamento'
            )}
          </Button>
        </div>

        {/* ── Corpo ── */}
        <div className="flex-1 p-6 space-y-6 w-full">

          {/* Cabeçalho comercial */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cliente — read-only */}
            <FormField
              control={form.control}
              name="customer_id"
              render={() => (
                <FormItem>
                  <FormLabel>Cliente</FormLabel>
                  <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                    {customerName}
                  </div>
                </FormItem>
              )}
            />

            {/* Válido até */}
            <FormField
              control={form.control}
              name="valid_until"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Válido até *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          {field.value ? format(field.value, 'dd/MM/yyyy') : 'Selecione'}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Condição de Pagamento */}
            <FormField
              control={form.control}
              name="payment_condition_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condição de Pagamento</FormLabel>
                  <FormControl>
                    <PaymentConditionGroupedSelector
                      value={field.value || ''}
                      onChange={field.onChange}
                      totalValue={calculateTotal()}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Prazo de Entrega */}
            <FormField
              control={form.control}
              name="delivery_deadline"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prazo de Entrega</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: 10 dias, Imediato, A combinar..."
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── Itens agrupados por equipamento ── */}
          {equipment.map((eq) => {
            const eqItems = items
              .map((item, globalIdx) => ({ item, globalIdx }))
              .filter(({ item }) => item.service_order_equipment_id === eq.id);

            return (
              <div key={eq.id} className="border rounded-lg overflow-hidden">
                {/* Cabeçalho do equipamento */}
                <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">
                    {eq.equipment_name}
                    {eq.serial_number && (
                      <span className="text-muted-foreground font-normal ml-2">
                        — S/N: {eq.serial_number}
                      </span>
                    )}
                    {eq.brand && eq.model && (
                      <span className="text-muted-foreground font-normal ml-1">
                        ({eq.brand} {eq.model})
                      </span>
                    )}
                  </span>
                </div>

                <div className="p-4 space-y-3">
                  {/* Cabeçalho da grade */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/30 rounded-lg font-medium text-sm">
                    <div className="col-span-1">Tipo</div>
                    <div className="col-span-4">Produto / Serviço</div>
                    <div className="col-span-1">Qtd</div>
                    <div className="col-span-2">Preço Unit.</div>
                    <div className="col-span-2">Desconto</div>
                    <div className="col-span-1">Total</div>
                    <div className="col-span-1" />
                  </div>

                  {eqItems.length === 0 && (
                    <p className="text-sm text-muted-foreground px-4 py-1">
                      Nenhum item. Adicione um abaixo.
                    </p>
                  )}

                  {eqItems.map(({ item, globalIdx }) => (
                    <div
                      key={globalIdx}
                      className="grid grid-cols-12 gap-2 p-3 border rounded-lg hover:border-primary/50 transition-colors"
                    >
                      {/* Tipo */}
                      <div className="col-span-1">
                        <Select
                          value={item.type}
                          onValueChange={(v) =>
                            updateItemBatch(globalIdx, {
                              type:       v as 'product' | 'service',
                              product_id: '',
                              service_id: '',
                            })
                          }
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="product">Produto</SelectItem>
                            <SelectItem value="service">Serviço</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Produto / Serviço */}
                      <div className="col-span-4 flex items-center gap-1">
                        <div className="flex-1">
                          <ProductServiceCombobox
                            value={
                              item.type === 'product' ? item.product_id : item.service_id
                            }
                            onProductSelected={
                              item.type === 'product'
                                ? (product) => handleProductSelected(globalIdx, product)
                                : undefined
                            }
                            onChange={(value, details) => {
                              if (item.type === 'product') {
                                // Produto: atualiza inline (modal abre via onProductSelected)
                                const updates: Partial<ItemState> = { product_id: value };
                                if (details?.sale_price && details.sale_price > 0) {
                                  updates.unit_price = Number(details.sale_price);
                                } else {
                                  updates.unit_price = 0;
                                }
                                updateItemBatch(globalIdx, updates);
                              } else if (value && details) {
                                // Serviço: abre o ServiceSelectionModal (S1)
                                handleServiceSelected(
                                  globalIdx,
                                  value,
                                  details.name,
                                  details.sale_price || 0,
                                );
                              }
                            }}
                            type={item.type}
                          />
                        </div>
                        {/* Botão de editar linha de serviço existente */}
                        {item.type === 'service' && item.service_id && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground"
                            title="Editar item"
                            onClick={() => handleEditService(globalIdx, item)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>

                      {/* Qtd */}
                      <div className="col-span-1">
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.001"
                          min="0.001"
                          className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={item.quantity || ''}
                          onChange={(e) =>
                            updateItemBatch(globalIdx, {
                              quantity: parseFloat(e.target.value) || 0.001,
                            })
                          }
                        />
                      </div>

                      {/* Preço Unit. (read-only — alterado via ProductSelectionModal) */}
                      <div className="col-span-2">
                        <div className="h-10 flex items-center px-3 bg-muted border rounded-md text-sm">
                          {formatCurrency(item.unit_price || 0)}
                        </div>
                      </div>

                      {/* Desconto (read-only) */}
                      <div className="col-span-2">
                        <div className="h-10 flex items-center px-3 bg-muted border rounded-md text-sm">
                          {item.discount_value > 0 || item.discount_percentage > 0 ? (
                            <span className="text-red-600 font-medium">
                              -{formatCurrency(
                                item.discount_value > 0
                                  ? item.discount_value * (item.quantity || 1)
                                  : (item.quantity * item.unit_price * item.discount_percentage) / 100,
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </div>

                      {/* Total */}
                      <div className="col-span-1 flex items-center">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {formatCurrency(calculateItemTotal(item))}
                          </span>
                          {(item.discount_value > 0 || item.discount_percentage > 0) && (
                            <span className="text-xs text-emerald-600 dark:text-emerald-400">
                              -{formatCurrency(
                                item.discount_value > 0
                                  ? roundMoney(item.discount_value * (item.quantity || 1))
                                  : roundMoney(
                                      (item.quantity * item.unit_price * item.discount_percentage) / 100,
                                    ),
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Remover */}
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(globalIdx)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addItemToEquipment(eq.id)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar item
                  </Button>
                </div>
              </div>
            );
          })}

          {/* ── Desconto global ── */}
          <DiscountSelector
            subtotal={calculateSubtotal()}
            discountPercentage={form.watch('discount_percentage') || 0}
            discountValue={form.watch('discount_value') || 0}
            onDiscountChange={(percentage, value) => {
              form.setValue('discount_percentage', percentage);
              form.setValue('discount_value', value);
            }}
            compact
          />

          {/* ── Frete ── */}
          <div className="flex flex-col md:flex-row gap-4">
            <FormField
              control={form.control}
              name="freight_value"
              render={({ field }) => (
                <FormItem className="w-full md:w-64">
                  <FormLabel className="flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Frete
                  </FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value || 0}
                      onChange={field.onChange}
                      placeholder="R$ 0,00"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── Resumo financeiro ── */}
          <div className="flex flex-col items-end border-t pt-4 space-y-1">
            <div className="flex justify-between w-full max-w-md text-base">
              <span className="text-muted-foreground">Subtotal (sem descontos):</span>
              <span className="font-medium">
                {formatCurrency(calculateOrderSummary(items).grossSubtotal)}
              </span>
            </div>
            {calculateTotalDiscount() > 0 && (
              <div className="flex justify-between w-full max-w-md text-base">
                <span className="text-muted-foreground">Total de Descontos:</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  -{formatCurrency(calculateTotalDiscount())}
                </span>
              </div>
            )}
            {(form.watch('freight_value') || 0) > 0 && (
              <div className="flex justify-between w-full max-w-md text-base">
                <span className="text-muted-foreground">Frete:</span>
                <span className="font-medium">
                  +{formatCurrency(form.watch('freight_value') || 0)}
                </span>
              </div>
            )}
            <div className="flex justify-between w-full max-w-md text-xl font-bold border-t pt-2 mt-2">
              <span>Total do Orçamento:</span>
              <span>{formatCurrency(calculateTotal())}</span>
            </div>
          </div>

          {/* ── Observações ── */}
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observações</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={3}
                    placeholder="Observações gerais sobre o orçamento"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fiscal_notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observações Complementares à Nota Fiscal</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={3}
                    placeholder="Informações adicionais que devem constar na nota fiscal"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Rodapé de ação ── */}
          <div className="flex justify-between pt-4 pb-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/dashboard/services/${id}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para OS
            </Button>
            <Button type="submit" disabled={updateQuotation.isPending}>
              {updateQuotation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando…
                </>
              ) : (
                'Salvar Orçamento'
              )}
            </Button>
          </div>
        </div>
      </form>

      {/* ProductSelectionModal — abre ao clicar no nome de um produto */}
      <ProductSelectionModal
        open={productModalOpen}
        onOpenChange={setProductModalOpen}
        product={selectedProductForModal}
        customerId={order?.customer_id}
        onConfirm={handleModalConfirm}
      />

      {/* ServiceSelectionModal — abre ao selecionar/editar serviço (S1) */}
      <ServiceSelectionModal
        open={serviceModalOpen}
        onOpenChange={setServiceModalOpen}
        service={selectedServiceForModal}
        costPrice={serviceCostPrice}
        initialValues={serviceInitialValues}
        onConfirm={handleServiceModalConfirm}
      />
    </Form>
  );
}
